import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = '/api';
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState(null);

  useEffect(() => {
    init();
  }, []);

  async function init() {
    // Check URL for token (from Entra callback redirect)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      localStorage.setItem("sf_token", urlToken);
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Fetch auth config
    try {
      const cfg = await axios.get(`${API}/auth/config`);
      setAuthConfig(cfg.data);
    } catch {
      setAuthConfig({ entra_configured: false, dev_bypass: true });
    }

    // Check stored token
    const token = localStorage.getItem("sf_token");
    if (token) {
      try {
        const resp = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(resp.data);
      } catch {
        localStorage.removeItem("sf_token");
      }
    } else {
      // Try dev bypass auto-login
      try {
        const resp = await axios.get(`${API}/auth/me`);
        if (resp.data && resp.data.name) {
          setUser(resp.data);
          if (resp.data.dev_bypass) {
            // Get a proper token for dev mode
            const loginResp = await axios.get(`${API}/auth/login`);
            if (loginResp.data?.token) {
              localStorage.setItem("sf_token", loginResp.data.token);
            }
          }
        }
      } catch {
        // Not authenticated
      }
    }
    setLoading(false);
  }

  async function login() {
    try {
      const resp = await axios.get(`${API}/auth/login`);
      if (resp.data?.token) {
        // Dev bypass — direct token
        localStorage.setItem("sf_token", resp.data.token);
        setUser(resp.data.user);
      }
    } catch {
      // Entra redirect would happen via 302; for direct call, redirect manually
      window.location.href = `${API}/auth/login`;
    }
  }

  function logout() {
    const token = localStorage.getItem("sf_token");
    if (token) {
      axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem("sf_token");
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, authConfig, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
